import React, { Fragment } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";

const PrivateCloudPage: React.FC = () => {
    return (
        <Fragment>
            <Helmet>
                <title>Private Cloud Solutions | AquarLabs</title>
                <meta name="description" content="Deploy secure, dedicated cloud infrastructure tailored to your organization. AquarLabs' private cloud solutions offer maximum control, compliance, and performance." />
                <meta name="keywords" content="private cloud, dedicated cloud, enterprise cloud, secure cloud, cloud hosting, on-premise cloud, AquarLabs" />
            </Helmet>
            <div className="about-page inner-page">
                <div className="body_wrap o-clip">
                    <Header />
                    <main>
                        <PageTitle pageTitle="Private Cloud Solutions" pagesub="Private Cloud" parentPage="Solutions" parentLink="/service" />

                        {/* Hero Section */}
                        <section className="service-hero-section" style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.8) 100%)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-8">
                                        <h1 style={{ fontSize: "3rem", marginBottom: "20px", color: "#fff" }}>Private Cloud Solutions</h1>
                                        <p style={{ fontSize: "1.25rem", color: "rgba(255,255,255,0.8)", lineHeight: "1.8" }}>
                                            Experience the power of cloud computing with complete control over your infrastructure. Our private cloud solutions deliver enterprise-grade performance with uncompromising security.
                                        </p>
                                        <Link to="/contact-us" className="thm-btn agency-btn" style={{ marginTop: "30px", display: "inline-block" }}>
                                            <span className="text">Get Started</span>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Overview Section */}
                        <section style={{ padding: "80px 0" }}>
                            <div className="container">
                                <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Overview</span>
                                <h2 style={{ fontSize: "2.5rem", margin: "15px 0 30px", color: "#fff" }}>Dedicated Infrastructure, Maximum Control</h2>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)" }}>
                                    AquarLabs builds private cloud environments that give your organization exclusive access to computing resources with complete isolation from other tenants. Our solutions are designed for enterprises that require stringent security, regulatory compliance, and predictable performance for mission-critical workloads.
                                </p>
                                <p style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "rgba(255,255,255,0.8)", marginTop: "20px" }}>
                                    From bare-metal deployments to fully virtualized environments, we architect private clouds that align with your governance policies while delivering the agility and automation of modern cloud platforms.
                                </p>
                            </div>
                        </section>

                        {/* Services Section */}
                        <section style={{ padding: "80px 0", background: "rgba(0,2,15,0.3)" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-lg-4">
                                        <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase" }}>Our Capabilities</span>
                                        <h2 style={{ fontSize: "2rem", margin: "15px 0", color: "#fff" }}>Complete Private Cloud Services</h2>
                                    </div>
                                    <div className="col-lg-8">
                                        <div className="row">
                                            {[
                                                {
                                                    title: "Infrastructure Design",
                                                    desc: "Custom-built private cloud architectures for your specific needs.",
                                                    items: ["Hardware Selection & Procurement", "Network Architecture Design", "Storage Solution Planning"]
                                                },
                                                {
                                                    title: "Virtualization Platforms",
                                                    desc: "Deploy industry-leading hypervisors and container platforms.",
                                                    items: ["VMware vSphere", "Microsoft Hyper-V", "OpenStack & Kubernetes"]
                                                },
                                                {
                                                    title: "Security & Compliance",
                                                    desc: "Enterprise-grade security meeting strict regulatory requirements.",
                                                    items: ["Zero Trust Architecture", "Encryption at Rest & Transit", "Compliance Audit Support"]
                                                },
                                                {
                                                    title: "Operations & Support",
                                                    desc: "Managed services to keep your private cloud running optimally.",
                                                    items: ["24/7 Monitoring", "Patch Management", "Capacity Planning"]
                                                }
                                            ].map((activity, index) => (
                                                <div key={index} className="col-md-6" style={{ marginBottom: "30px" }}>
                                                    <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "30px", height: "100%" }}>
                                                        <h3 style={{ fontSize: "1.25rem", color: "#fff", marginBottom: "15px" }}>
                                                            <span style={{ color: "#fc9038" }}>●</span> {activity.title}
                                                        </h3>
                                                        <p style={{ fontSize: "0.95rem", color: "rgba(255,255,255,0.7)", marginBottom: "15px" }}>{activity.desc}</p>
                                                        <ul style={{ listStyle: "none", padding: 0 }}>
                                                            {activity.items.map((item, i) => (
                                                                <li key={i} style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", padding: "5px 0" }}>→ {item}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Why Us Section */}
                        <section style={{ padding: "80px 0", background: "#0a0a0a" }}>
                            <div className="container">
                                <div className="text-center" style={{ marginBottom: "50px" }}>
                                    <span style={{ color: "#fc9038", fontSize: "14px", textTransform: "uppercase", letterSpacing: "2px" }}>Why Us</span>
                                    <h2 style={{ fontSize: "2.5rem", margin: "15px 0 20px", color: "#fff" }}>Why Choose AquarLabs for Private Cloud?</h2>
                                    <p style={{ fontSize: "1rem", color: "rgba(255,255,255,0.6)", maxWidth: "700px", margin: "0 auto" }}>
                                        We bring decades of enterprise infrastructure expertise to every private cloud deployment.
                                    </p>
                                </div>
                                <div className="row">
                                    {[
                                        { title: "Complete Isolation", desc: "Dedicated resources with no multi-tenancy concerns or noisy neighbors." },
                                        { title: "Regulatory Compliance", desc: "Meet HIPAA, PCI-DSS, SOC2, and industry-specific requirements." },
                                        { title: "Predictable Performance", desc: "Guaranteed compute, storage, and network performance SLAs." },
                                        { title: "Data Sovereignty", desc: "Keep data in specific geographic locations as required by law." },
                                        { title: "Custom Configurations", desc: "Tailor every aspect of your infrastructure to exact specifications." },
                                        { title: "Seamless Integration", desc: "Connect with existing on-premise systems and third-party services." }
                                    ].map((item, index) => (
                                        <div key={index} className="col-lg-6" style={{ marginBottom: "20px" }}>
                                            <div style={{
                                                background: "rgba(20, 20, 20, 0.8)",
                                                border: "1px solid rgba(252, 144, 56, 0.4)",
                                                borderRadius: "16px",
                                                padding: "28px 30px",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "15px"
                                            }}>
                                                <div style={{
                                                    minWidth: "40px",
                                                    height: "40px",
                                                    borderRadius: "8px",
                                                    background: "rgba(252, 144, 56, 0.1)",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}>
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#fc9038" strokeWidth="2" />
                                                        <path d="M9 12L11 14L15 10" stroke="#fc9038" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <h3 style={{ fontSize: "1.1rem", color: "#fff", marginBottom: "8px", fontWeight: "600" }}>{item.title}</h3>
                                                    <p style={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.6)", margin: 0, lineHeight: "1.6" }}>{item.desc}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section style={{ padding: "80px 0", background: "linear-gradient(135deg, rgba(252, 144, 56, 0.1) 0%, rgba(0, 2, 15, 0.5) 100%)" }}>
                            <div className="container text-center">
                                <h2 style={{ fontSize: "2.5rem", color: "#fff", marginBottom: "20px" }}>Build Your Private Cloud Today</h2>
                                <p style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)", marginBottom: "30px" }}>
                                    Let's design a secure, high-performance private cloud tailored to your enterprise.
                                </p>
                                <Link to="/contact-us" className="thm-btn agency-btn">
                                    <span className="text">Contact Us Today</span>
                                </Link>
                            </div>
                        </section>
                    </main>
                    <Footer />
                    <Scrollbar />
                </div>
            </div>
        </Fragment>
    );
};

export default PrivateCloudPage;
